import { formatDistanceToNow } from 'date-fns';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { Note } from 'app/agriculture/queries/notes/types/Note';
import { ProfileAvatar } from 'app/agriculture/infobox/common/ProfileAvatar';
import { CalendarIcon, LocationIcon } from 'app/agriculture/icons';
import { Button } from 'app/agriculture/components/Button/Button';
import { NoteRowHeaderItem } from './NoteRowHeaderItem';
import './NoteRow.scss';
import { useGenericEntityFromId } from 'app/agriculture/queries/entities/useGenericEntityFromId';
import { ExpandableTypography } from 'app/agriculture/infobox/components/ExpandableTypography/ExpandableTypography';
import { useUsers } from 'app/agriculture/hooks/users/useUsers';

interface NoteRowProps {
    note: Note;
}

export const NoteRow = ({ note }: NoteRowProps) => {
    const { text, creatorId, createdAt, id, location } = note;
    const routeActions = useRouteActions();
    const getParentEntity = useGenericEntityFromId();
    const parent = getParentEntity(note.parentId);
    const creator = useUsers().find((user) => user.id === creatorId);
    const creatorName = creator?.name ?? 'Unknown';

    return (
        <div className="NoteRow" data-testid="NoteRow">
            <h5>{parent?.name ?? 'The Farm'}</h5>
            <div className="NoteRow__header">
                <NoteRowHeaderItem
                    className="NoteRow__name"
                    text={creatorName}
                    icon={<ProfileAvatar name={creatorName} />}
                    tooltip={creatorName}
                />
                <NoteRowHeaderItem
                    className="NoteRow__timestamp"
                    text={formatDistanceToNow(createdAt, { addSuffix: true })}
                    icon={<CalendarIcon />}
                    tooltip={createdAt.toLocaleString()}
                />
                <NoteRowHeaderItem
                    className="NoteRow__location"
                    text="Location"
                    icon={<LocationIcon />}
                    disabled={!location}
                />
                <Button
                    text="Edit"
                    startIcon="edit"
                    variant="compact"
                    onClick={() => {
                        routeActions.edit(id, { type: 'Note' });
                    }}
                />
            </div>
            <ExpandableTypography id="note-text" text={text} maxLines={3} disableExpandButton />
            <Button
                variant="compact"
                text="Show Details"
                endIcon="goto"
                onClick={() => {
                    routeActions.goto(id, { type: 'Note' });
                }}
            />
        </div>
    );
};
